import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Shield, Building2, Sparkles } from 'lucide-react';
import Reveal from '../components/site/Reveal';
import SectionHeader from '../components/site/SectionHeader';
import NeuralBackground from '../components/site/NeuralBackground';
import SectorWheel from '../components/site/SectorWheel';
import { BRAND, PRINCIPLES, STATS } from '../data/site';
import { useT } from '../i18n/LanguageContext';
import { useHeroImages } from '../lib/useHeroImages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t } = useT();
  const heroes = useHeroImages();
  const [pillars, setPillars] = useState([]);
  const [m3, setM3] = useState([]);
  const [subs, setSubs] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pillars`).then((r) => setPillars(r.data || [])).catch(() => {});
    axios.get(`${API}/ventures`).then((r) => {
      const all = r.data || [];
      setM3(all.filter((v) => v.category === 'm3'));
      setSubs(all.filter((v) => v.category === 'subsidiary'));
      setPortfolio(all.filter((v) => v.category === 'portfolio'));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Neural net always renders as the fallback / loading layer */}
          <NeuralBackground className="absolute inset-0 w-full h-full" density={1.1} />
          {/* If admin set a video, play it on top (fades in when ready) */}
          {heroes.homeVideo && (
            <video
              key={heroes.homeVideo}
              data-testid="hero-video"
              src={heroes.homeVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onCanPlay={() => setVideoReady(true)}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-out"
              style={{
                opacity: videoReady ? 0.55 : 0,
                transform: 'scale(1.06)',
                animation: videoReady ? 'kenBurns 22s ease-in-out infinite alternate' : 'none',
              }}
            />
          )}
          {/* Left-side readability vignette (theme-aware) */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'var(--hero-vignette)',
          }} />
          <div className="absolute inset-x-0 bottom-0 h-48 pointer-events-none" style={{
            background: 'linear-gradient(to bottom, transparent, var(--bg-base))',
          }} />
          <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="eyebrow flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-brand-teal-light pulse-dot" />
                  {t('hero.eyebrow')}
                </p>
              </Reveal>
              <Reveal delay={120}>
                <h1 className="mt-6 font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim">
                  {t('hero.titleA')}<br />
                  <span className="text-brand-teal-light italic font-light">{t('hero.titleB')}</span> {t('hero.titleC')}<br />
                  {t('hero.titleD')}
                </h1>
              </Reveal>
              <Reveal delay={240}>
                <p className="mt-8 text-sec text-lg md:text-xl max-w-2xl leading-relaxed">
                  {t('hero.sub')}
                </p>
              </Reveal>
              <Reveal delay={360}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/ventures" data-testid="hero-cta-ventures" className="btn-primary">
                    {t('common.exploreVentures')} <ArrowRight size={16} />
                  </Link>
                  <Link to="/strategy" data-testid="hero-cta-strategy" className="btn-secondary">
                    {t('common.readOurStrategy')}
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-4 hidden lg:block">
              <Reveal delay={500}>
                <div className="relative aspect-square shimmer-border p-1">
                  <div className="absolute inset-1 rounded-xl overflow-hidden surface flex flex-col items-center justify-center p-8 text-center">
                    <img src={BRAND.logo} alt="MBK" className="h-32 w-auto mb-6" />
                    <p className="font-arabic text-3xl text-brand-teal-light/90">مَجْمُوعَة</p>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-sec uppercase mt-3">MBK · Holding · Group</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Stats strip */}
          <Reveal delay={700}>
            <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-subtle">
              {STATS.map((s) => (
                <div key={s.label} className="surface p-6 md:p-8">
                  <p className="font-display text-3xl md:text-4xl text-brand-teal-light tracking-tight">{s.value}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted font-mono">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTOR WHEEL — first thing after hero, like invest.qa */}
      <section data-testid="home-sector-section" className="py-24 md:py-32 border-t border-subtle relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <div className="max-w-3xl">
              <p className="eyebrow mb-5">— Where We Invest</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-prim tracking-tighter leading-[1.05]">
                Eight sectors,<br />
                <span className="italic text-brand-teal-light font-light">one country.</span>
              </h2>
              <p className="mt-6 text-sec text-lg leading-relaxed">
                Hover or click any slice to see where MBK is investing inside Qatar's economy — and which of our companies are advancing that sector today.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-16">
              <SectorWheel />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader
              eyebrow="— Philosophy"
              title={<>Three principles,<br />held in equal weight.</>}
              lede="Three virtues — rendered through restraint, mastery and patience. The standards by which we underwrite, build and steward."
              testid="home-philosophy-header"
            />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 120}>
                <div className="group card-glow border border-subtle surface-2 p-8 md:p-10 h-full rounded-md">
                  <p className="font-arabic text-5xl text-brand-teal-light/80 mb-6">{p.arabic}</p>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-muted uppercase">0{i+1} · Principle</p>
                  <h3 className="font-display text-2xl text-prim mt-3 tracking-tight">{p.title}</h3>
                  <p className="mt-4 text-sec leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* NATIONAL STRATEGY SUMMARY */}
      <section className="py-24 md:py-32 relative border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <Reveal>
                <p className="eyebrow mb-5">— Strategic Framework</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-prim tracking-tighter leading-[1.05]">
                  Anchored in Doha. Aligned with Qatar's national ambition.
                </h2>
                <p className="mt-6 text-sec text-lg leading-relaxed">
                  Every company under our roof is built to advance the diversification, innovation and human-capital pillars of Qatar National Vision 2030 and the Third National Development Strategy (NDS3) 2024–2030.
                </p>
                <Link to="/strategy" data-testid="home-strategy-cta" className="btn-secondary mt-8">
                  See full strategy mapping <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pillars.slice(0, 6).map((p, i) => (
                  <Reveal key={p.id} delay={i * 80}>
                    <div className="border border-subtle surface-2 p-6 card-glow rounded-md h-full">
                      <div className="flex items-baseline justify-between">
                        <span className="font-mono text-xs text-brand-teal-light">{p.n}</span>
                        <span className="font-arabic text-xl text-muted">{p.arabic}</span>
                      </div>
                      <h4 className="font-display text-lg text-prim mt-4 tracking-tight">{p.title}</h4>
                      <p className="mt-2 text-sm text-sec leading-relaxed">{p.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VENTURES PREVIEW */}
      <section className="py-24 md:py-32 relative border-t border-subtle bg-base">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <Reveal>
              <SectionHeader
                eyebrow="— Portfolio Architecture"
                title={<>Held under one quiet roof.</>}
                lede="A concentrated holding spanning subsidiaries, M³ ventures and growth-stage portfolio companies — across health, hardware, security, aerospace, fintech and digital."
                testid="home-ventures-header"
              />
            </Reveal>
            <Reveal delay={150}>
              <Link to="/ventures" data-testid="home-ventures-cta" className="btn-secondary">
                See all ventures <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          {/* Bento Grid */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Featured large */}
            <Reveal className="md:col-span-8 md:row-span-2" delay={0}>
              <div className="relative h-full min-h-[360px] overflow-hidden rounded-md border border-subtle group card-glow">
                <img src="https://images.pexels.com/photos/30547576/pexels-photo-30547576.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-black/40 to-transparent" />
                <div className="relative p-8 md:p-12 h-full flex flex-col justify-between">
                  <div>
                    <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> M³ Holdings Group</p>
                    <p className="mt-4 font-arabic text-4xl text-brand-teal-light">إِسْتِثْمَار</p>
                  </div>
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl text-prim tracking-tight">Sovereign Influence,<br />Industrial Mastery.</h3>
                    <p className="mt-4 text-sec max-w-lg">M³ Ventures is the investment arm — a concentrated portfolio of operating companies across health, hardware, security and intelligence.</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal className="md:col-span-4" delay={120}>
              <div className="border border-subtle surface-2 p-6 rounded-md card-glow h-full">
                <p className="eyebrow"><Building2 size={12} className="inline mr-2" />Subsidiaries</p>
                <p className="font-display text-5xl text-prim mt-3 tracking-tighter">{subs.length}</p>
                <p className="text-sec mt-2 text-sm">Operating companies across Qatar and abroad.</p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-4" delay={200}>
              <div className="border border-subtle surface-2 p-6 rounded-md card-glow h-full">
                <p className="eyebrow">M³ Ventures</p>
                <p className="font-display text-5xl text-prim mt-3 tracking-tighter">{m3.length}</p>
                <p className="text-sec mt-2 text-sm">Three in operations, three in concept.</p>
              </div>
            </Reveal>

            {m3.slice(0, 3).map((v, i) => (
              <Reveal key={v.id} className="md:col-span-4" delay={280 + i * 80}>
                <div className="border border-subtle surface-2 p-6 rounded-md card-glow h-full flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="font-arabic text-2xl text-brand-teal-light/80">{v.arabic}</span>
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">{v.status}</span>
                  </div>
                  <h4 className="font-display text-xl text-prim mt-3 tracking-tight">{v.name}</h4>
                  <p className="font-mono text-[11px] text-brand-teal-light/80 mt-1">{v.sector}</p>
                  <p className="mt-3 text-sm text-sec leading-relaxed">{v.blurb}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Marquee */}
          <Reveal>
            <div className="mt-16 border-y border-subtle py-8 overflow-hidden">
              <p className="eyebrow text-center mb-6">— Portfolio Companies</p>
              <div className="relative overflow-hidden">
                <div className="flex gap-12 marquee-track whitespace-nowrap">
                  {[...portfolio.map(v => v.name), ...portfolio.map(v => v.name)].map((name, i) => (
                    <span key={i} className="font-display text-2xl md:text-3xl text-muted hover:text-brand-teal-light transition-colors tracking-tight">
                      {name} <span className="text-brand-maroon-light mx-4">◆</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECURITY TEASER */}
      <section className="py-24 md:py-32 border-t border-subtle relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="eyebrow mb-5 flex items-center gap-2"><Shield size={14} /> Bank-grade Security</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-prim tracking-tighter leading-[1.05]">
                  Custody you can <span className="italic text-brand-teal-light">audit</span>.
                </h2>
                <p className="mt-6 text-sec text-lg leading-relaxed max-w-xl">
                  Our portfolio is governed with the same security posture as global financial institutions. ISO/IEC 27001, SOC 2 Type II processes, AES-256 encryption, zero-trust architecture and a 24/7 Security Operations Centre.
                </p>
                <Link to="/security" data-testid="home-security-cta" className="btn-secondary mt-8">
                  Inspect the controls <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={200}>
                <div className="relative aspect-[5/4] shimmer-border p-1">
                  <div className="absolute inset-1 rounded-xl overflow-hidden">
                    <img src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Bank-grade data centre" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#020617]/90 via-transparent to-[#5C1A2B]/30" />
                    <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-2 text-center">
                      {['ISO 27001', 'SOC 2', 'AES-256'].map((b) => (
                        <div key={b} className="glass px-3 py-2 rounded font-mono text-[10px] tracking-[0.15em] uppercase text-brand-teal-light">{b}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-subtle relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 text-center">
          <Reveal>
            <p className="font-arabic text-4xl md:text-5xl text-brand-teal-light/80">بِالأَمَانَةِ نَبْدَأ، وَبِالعَدْلِ نَخْتِم</p>
            <p className="mt-4 italic text-sec">"Trust to start, fairness to last."</p>
            <p className="mt-2 text-xs uppercase tracking-[0.3em] text-muted font-mono">— Directive of the Patron</p>
            <h2 className="mt-12 font-display text-4xl md:text-5xl lg:text-6xl text-prim tracking-tighter leading-[1.05] max-w-3xl mx-auto">
              The door is always slightly open.
            </h2>
            <p className="mt-6 text-sec max-w-xl mx-auto">
              Inquiries from operators, limited partners and institutional partners are read personally — and answered in the same hand.
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Link to="/contact" data-testid="home-final-cta" className="btn-primary">Submit an inquiry <ArrowRight size={14} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
